import { Box, Flex, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import {
  ChevronDownIcon,
  FileCheck2Icon,
  FileWarningIcon,
  ListChecksIcon,
} from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { ComplianceDocumentSummary } from './types';

interface DocumentListProps {
  documents: ComplianceDocumentSummary[];
  selectedDocumentId?: string | null;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * A checklist-activity group: the SkySlope checklist activity and every
 * ingestion document that belongs to it. Resubmitting acts on the whole
 * activity, so the rail groups documents this way.
 */
interface ChecklistGroup {
  activityId: string;
  activityName: string;
  documents: ComplianceDocumentSummary[];
}

/** An entry within a folder: either a checklist-activity group or a lone doc. */
export type ChecklistEntry =
  | { kind: 'group'; group: ChecklistGroup }
  | { kind: 'single'; document: ComplianceDocumentSummary };

/**
 * A SkySlope checklist GROUP (folder) — the top-level grouping. Holds the
 * activity groups / standalone documents that belong to it.
 */
interface FolderGroup {
  folderName: string;
  entries: ChecklistEntry[];
}

// "Needs review" is computed on the backend (`needsReviewByAgent`): the document
// is rejected (SkySlope checklist status "Incomplete") AND not resubmitted.
// Resubmitting acts on the whole checklist activity, so every document in the
// activity drops out of "Needs Review" — until a fresh, not-yet-resubmitted
// rejected document arrives in the same activity, which pulls the group back.
const needsReview = (document: ComplianceDocumentSummary): boolean =>
  document.needsReviewByAgent;

const entryNeedsReview = (entry: ChecklistEntry): boolean =>
  entry.kind === 'group'
    ? entry.group.documents.some(needsReview)
    : needsReview(entry.document);

/**
 * Synthetic activity id + label for the single group that collects every
 * non-checklist document. It is rendered exactly like a checklist-activity
 * group, but always sits LAST in the "Other Documents" section.
 */
const NO_CHECKLIST_ACTIVITY_ID = '__no-checklist__';
const NO_CHECKLIST_GROUP_NAME = 'No checklist';

/**
 * A document belongs to a SkySlope checklist activity only when it has BOTH an
 * activity id (the grouping key) and a non-empty activity name. Everything else
 * is a "no checklist" document, gathered into the single trailing group above.
 */
const isChecklistDocument = (document: ComplianceDocumentSummary): boolean =>
  document.skyslopeActivityId != null &&
  document.skyslopeActivityName != null &&
  document.skyslopeActivityName !== '';

/**
 * Groups a folder's documents by checklist activity (`skyslopeActivityId`).
 * Documents without an activity become standalone entries. Order follows the
 * incoming `documentUpdatedAt` desc order (group positioned at its first member).
 */
const buildChecklistEntries = (
  documents: ComplianceDocumentSummary[]
): ChecklistEntry[] => {
  const entries: ChecklistEntry[] = [];
  const groupByActivityId = new Map<string, ChecklistGroup>();

  for (const document of documents) {
    const activityId = document.skyslopeActivityId;
    if (activityId == null) {
      entries.push({ kind: 'single', document });
      continue;
    }

    let group = groupByActivityId.get(activityId);
    if (!group) {
      group = {
        activityId,
        activityName: document.skyslopeActivityName ?? document.documentName,
        documents: [],
      };
      groupByActivityId.set(activityId, group);
      entries.push({ kind: 'group', group });
    }
    group.documents.push(document);
  }

  return entries;
};

/**
 * Reorders a folder's entries so checklist-activity groups come first and
 * standalone documents sink to the bottom — stable, preserving order within
 * each partition.
 */
const groupsFirst = (entries: ChecklistEntry[]): ChecklistEntry[] => [
  ...entries.filter((entry) => entry.kind === 'group'),
  ...entries.filter((entry) => entry.kind === 'single'),
];

/**
 * Builds the rail's folder groups from the (already `documentUpdatedAt` desc)
 * document list: top-level by `folderName` (SkySlope checklist group), then by
 * checklist activity within each folder. Folders/groups are positioned at their
 * first (most-recent) member.
 */
export const buildFolderGroups = (
  documents: ComplianceDocumentSummary[]
): FolderGroup[] => {
  const folderByName = new Map<string, ComplianceDocumentSummary[]>();
  const order: string[] = [];

  for (const document of documents) {
    const folderName = document.folderName ?? '';
    if (!folderByName.has(folderName)) {
      folderByName.set(folderName, []);
      order.push(folderName);
    }
    folderByName.get(folderName)!.push(document);
  }

  return order.map((folderName) => ({
    folderName,
    entries: groupsFirst(buildChecklistEntries(folderByName.get(folderName)!)),
  }));
};

/**
 * Narrows each folder to the entries matching `predicate`, dropping folders
 * left with no entries. Used to split the checklist-activity groups across the
 * Needs-agent-review / Other-Documents categories: an activity group is kept
 * whole (it needs review if any of its docs does), so a folder header can appear
 * in both categories when it owns groups on both sides. Non-checklist documents
 * never reach this function — they are collected separately into the trailing
 * "No checklist" group.
 */
const filterFoldersByEntries = (
  folders: FolderGroup[],
  predicate: (entry: ChecklistEntry) => boolean
): FolderGroup[] =>
  folders
    .map((folder) => ({
      ...folder,
      entries: folder.entries.filter(predicate),
    }))
    .filter((folder) => folder.entries.length > 0);

interface DocumentStatusIconProps {
  document: ComplianceDocumentSummary;
  /** Color for the (clean) file-check icon; the warning icon is always yellow. */
  cleanColor: string;
}

/**
 * Per-document leading icon: a yellow warning when the document has open
 * suggestions, otherwise a file-check tinted to match its row. Status is keyed
 * on the individual document, independent of selection.
 */
const DocumentStatusIcon: FC<DocumentStatusIconProps> = ({
  document,
  cleanColor,
}) =>
  needsReview(document) ? (
    <Icon
      as={FileWarningIcon}
      boxSize="16px"
      color="compliance.status.warning"
      data-testid="doc-status-needs-review"
      flexShrink={0}
    />
  ) : (
    <Icon
      as={FileCheck2Icon}
      boxSize="16px"
      color={cleanColor}
      data-testid="doc-status-clean"
      flexShrink={0}
    />
  );

interface ChecklistDocumentRowProps {
  document: ComplianceDocumentSummary;
  isSelected: boolean;
  onSelect: (ingestionDocumentId: string) => void;
}

/** A document row nested inside an expanded checklist-activity group. */
const ChecklistDocumentRow: FC<ChecklistDocumentRowProps> = ({
  document,
  isSelected,
  onSelect,
}) => (
  <Flex
    align="center"
    aria-current={isSelected ? 'true' : undefined}
    cursor="pointer"
    gap={1.5}
    overflow="hidden"
    role="button"
    tabIndex={0}
    onClick={() => onSelect(document.ingestionDocumentId)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(document.ingestionDocumentId);
      }
    }}
  >
    <DocumentStatusIcon
      cleanColor={
        isSelected ? 'compliance.text.primary' : 'compliance.text.muted'
      }
      document={document}
    />
    <Text
      color={isSelected ? 'compliance.text.primary' : 'compliance.text.muted'}
      fontSize="sm"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {document.documentName}
    </Text>
  </Flex>
);

interface ChecklistGroupRowProps {
  group: ChecklistGroup;
  isExpanded: boolean;
  selectedDocumentId?: string | null;
  onToggle: (activityId: string) => void;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * A collapsible checklist-activity group ("Checklist item"). Collapsed: just
 * the chevron + title. Expanded: an elevated card listing every document in the
 * activity (the open document highlighted, its siblings dimmed).
 */
const ChecklistGroupRow: FC<ChecklistGroupRowProps> = ({
  group,
  isExpanded,
  selectedDocumentId,
  onToggle,
  onSelectDocument,
}) => (
  <Box
    bg={isExpanded ? 'compliance.group.expanded.bg' : 'transparent'}
    borderRadius="8px"
    p={2.5}
  >
    <Flex
      align="center"
      cursor="pointer"
      gap={1.5}
      role="button"
      tabIndex={0}
      onClick={() => onToggle(group.activityId)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle(group.activityId);
        }
      }}
    >
      <Icon
        as={ChevronDownIcon}
        boxSize="16px"
        color={
          isExpanded ? 'compliance.text.primary' : 'compliance.text.secondary'
        }
        flexShrink={0}
        transform={isExpanded ? undefined : 'rotate(-90deg)'}
        transition="transform 0.15s ease"
      />
      <Text
        color={
          isExpanded ? 'compliance.text.primary' : 'compliance.text.secondary'
        }
        fontSize="sm"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {group.activityName}
      </Text>
    </Flex>
    {isExpanded && (
      <VStack align="stretch" mt={3} pl="22px" spacing={3}>
        {group.documents.map((document) => (
          <ChecklistDocumentRow
            key={document.ingestionDocumentId}
            document={document}
            isSelected={selectedDocumentId === document.ingestionDocumentId}
            onSelect={onSelectDocument}
          />
        ))}
      </VStack>
    )}
  </Box>
);

interface StandaloneDocumentRowProps {
  document: ComplianceDocumentSummary;
  isSelected: boolean;
  onSelect: (ingestionDocumentId: string) => void;
}

/**
 * A standalone (no SkySlope checklist activity) document row — rendered without
 * activity-group chrome, with the per-document status icon (warning vs check).
 */
const StandaloneDocumentRow: FC<StandaloneDocumentRowProps> = ({
  document,
  isSelected,
  onSelect,
}) => (
  <Flex
    _hover={{
      bg: isSelected ? 'compliance.selected.bg' : 'compliance.hover.bg',
    }}
    align="center"
    aria-current={isSelected ? 'true' : undefined}
    bg={isSelected ? 'compliance.selected.bg' : 'transparent'}
    borderRadius="md"
    cursor="pointer"
    gap={1.5}
    overflow="hidden"
    px={2.5}
    py={2}
    role="button"
    tabIndex={0}
    onClick={() => onSelect(document.ingestionDocumentId)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(document.ingestionDocumentId);
      }
    }}
  >
    <DocumentStatusIcon
      cleanColor={
        isSelected ? 'compliance.text.primary' : 'compliance.text.secondary'
      }
      document={document}
    />
    <Text
      color={
        isSelected ? 'compliance.text.primary' : 'compliance.text.secondary'
      }
      fontSize="sm"
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
    >
      {document.documentName}
    </Text>
  </Flex>
);

interface FolderSectionProps {
  folder: FolderGroup;
  selectedDocumentId?: string | null;
  isExpanded: (activityId: string) => boolean;
  onToggle: (activityId: string) => void;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * A SkySlope checklist GROUP ("Checklist") — a fixed (non-collapsible) section
 * label (list-checks icon + folder name) followed by its checklist-activity
 * groups and standalone documents.
 */
const FolderSection: FC<FolderSectionProps> = ({
  folder,
  selectedDocumentId,
  isExpanded,
  onToggle,
  onSelectDocument,
}) => (
  <VStack
    align="stretch"
    borderBottomWidth={1}
    borderColor="compliance.card.border"
    pb={3}
    spacing={2}
  >
    {folder.folderName !== '' && (
      <Flex align="center" gap={1.5} px={2.5}>
        <Icon
          as={ListChecksIcon}
          boxSize="14px"
          color="compliance.text.muted"
          flexShrink={0}
        />
        {/* Subordinate to the category header above: muted + small + uppercase
            so the SkySlope group reads as a sub-section, not a peer. */}
        <Heading
          color="compliance.text.muted"
          fontSize="xs"
          fontWeight="medium"
          letterSpacing="wider"
          overflow="hidden"
          size="headline"
          textOverflow="ellipsis"
          textTransform="uppercase"
          whiteSpace="nowrap"
        >
          {folder.folderName}
        </Heading>
      </Flex>
    )}
    <VStack align="stretch" spacing={1}>
      {folder.entries.map((entry) =>
        entry.kind === 'group' ? (
          <ChecklistGroupRow
            key={entry.group.activityId}
            group={entry.group}
            isExpanded={isExpanded(entry.group.activityId)}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={onSelectDocument}
            onToggle={onToggle}
          />
        ) : (
          <StandaloneDocumentRow
            key={entry.document.ingestionDocumentId}
            document={entry.document}
            isSelected={
              selectedDocumentId === entry.document.ingestionDocumentId
            }
            onSelect={onSelectDocument}
          />
        )
      )}
    </VStack>
  </VStack>
);

interface DocumentCategoryProps {
  title: string;
  folders: FolderGroup[];
  defaultExpanded: boolean;
  selectedDocumentId?: string | null;
  isExpanded: (activityId: string) => boolean;
  onToggle: (activityId: string) => void;
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/**
 * A collapsible top-level category ("Needs agent review" / "Other Documents").
 * The header (prominent, with a trailing chevron) toggles the whole category;
 * "Other Documents" starts collapsed. Renders nothing when it has no folders.
 */
const DocumentCategory: FC<DocumentCategoryProps> = ({
  title,
  folders,
  defaultExpanded,
  selectedDocumentId,
  isExpanded,
  onToggle,
  onSelectDocument,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (folders.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Flex
        align="center"
        cursor="pointer"
        justify="space-between"
        px={2.5}
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((previous) => !previous)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((previous) => !previous);
          }
        }}
      >
        <Heading
          color="compliance.text.primary"
          fontSize="md"
          fontWeight="semibold"
          size="headline"
        >
          {title}
        </Heading>
        {/* Chevron on the trailing edge — distinguishes the top-level category
            toggle from the leading-edge chevrons on checklist-item groups. */}
        <Icon
          as={ChevronDownIcon}
          boxSize="18px"
          color="compliance.text.secondary"
          flexShrink={0}
          transform={expanded ? undefined : 'rotate(-90deg)'}
          transition="transform 0.15s ease"
        />
      </Flex>
      {expanded && (
        <VStack align="stretch" spacing={4}>
          {folders.map((folder) => (
            <FolderSection
              key={folder.folderName}
              folder={folder}
              isExpanded={isExpanded}
              selectedDocumentId={selectedDocumentId}
              onSelectDocument={onSelectDocument}
              onToggle={onToggle}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

/**
 * The agent compliance review document rail. Documents split into two
 * collapsible categories — "Needs agent review" (open, by default) and "Other
 * Documents" (collapsed, by default). Checklist-activity documents are grouped
 * by SkySlope checklist GROUP (folderName, a fixed "Checklist" section label),
 * then by checklist ACTIVITY (a collapsible "Checklist item"), then individual
 * documents. Every NON-checklist document (no SkySlope activity) is gathered
 * into a single "No checklist" group rendered last in "Other Documents",
 * regardless of its own review status. Each row's leading icon reflects that
 * document's own status (a yellow warning when it has open suggestions and is
 * not resubmitted, a check otherwise). Ordering follows the incoming
 * `documentUpdatedAt` desc order.
 */
export const DocumentList: FC<DocumentListProps> = ({
  documents,
  selectedDocumentId,
  onSelectDocument,
}) => {
  // Only checklist-activity documents flow through the folder → activity
  // grouping; non-checklist documents are pulled out into a single group below.
  const folders = useMemo(
    () => buildFolderGroups(documents.filter(isChecklistDocument)),
    [documents]
  );

  // Every non-checklist document, gathered into one group. Rendered last in
  // "Other Documents" regardless of any individual document's review status.
  const noChecklistGroup = useMemo<ChecklistGroup | null>(() => {
    const noChecklistDocuments = documents.filter(
      (document) => !isChecklistDocument(document)
    );
    if (noChecklistDocuments.length === 0) {
      return null;
    }

    return {
      activityId: NO_CHECKLIST_ACTIVITY_ID,
      activityName: NO_CHECKLIST_GROUP_NAME,
      documents: noChecklistDocuments,
    };
  }, [documents]);

  const needsReviewFolders = useMemo(
    () => filterFoldersByEntries(folders, entryNeedsReview),
    [folders]
  );
  const otherFolders = useMemo(() => {
    const cleanFolders = filterFoldersByEntries(
      folders,
      (entry) => !entryNeedsReview(entry)
    );
    if (noChecklistGroup == null) {
      return cleanFolders;
    }

    // The "No checklist" group is a header-less folder appended LAST, so it sits
    // below every checklist group in Other Documents.
    return [
      ...cleanFolders,
      {
        folderName: '',
        entries: [{ kind: 'group' as const, group: noChecklistGroup }],
      },
    ];
  }, [folders, noChecklistGroup]);

  // The checklist activity that owns the currently-selected document — its
  // group is expanded by default. A selected non-checklist document expands the
  // "No checklist" group. Falls back to the first activity group (Needs Review
  // first) when nothing is selected.
  const defaultExpandedActivityId = useMemo(() => {
    const selectedDocument = documents.find(
      (document) => document.ingestionDocumentId === selectedDocumentId
    );
    if (selectedDocument != null && !isChecklistDocument(selectedDocument)) {
      return NO_CHECKLIST_ACTIVITY_ID;
    }
    const selectedActivityId = selectedDocument?.skyslopeActivityId;
    if (selectedActivityId != null) {
      return selectedActivityId;
    }
    for (const folder of [...needsReviewFolders, ...otherFolders]) {
      const firstGroup = folder.entries.find((entry) => entry.kind === 'group');
      if (firstGroup?.kind === 'group') {
        return firstGroup.group.activityId;
      }
    }

    return null;
  }, [documents, selectedDocumentId, needsReviewFolders, otherFolders]);

  // Manual expand/collapse overrides keyed by activity id; unset groups fall
  // back to the default-expanded group above.
  const [manualExpanded, setManualExpanded] = useState<Record<string, boolean>>(
    {}
  );

  const isExpanded = (activityId: string): boolean =>
    manualExpanded[activityId] ?? activityId === defaultExpandedActivityId;

  const onToggle = (activityId: string): void =>
    setManualExpanded((previous) => {
      // Derive the current state from the updater's `previous` snapshot, not the
      // render-time closure — otherwise rapid back-to-back toggles can both read
      // the same stale value and leave the group in the wrong state.
      const currentlyExpanded =
        previous[activityId] ?? activityId === defaultExpandedActivityId;

      return {
        ...previous,
        [activityId]: !currentlyExpanded,
      };
    });

  if (needsReviewFolders.length === 0 && otherFolders.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={6}>
      <DocumentCategory
        defaultExpanded
        folders={needsReviewFolders}
        isExpanded={isExpanded}
        selectedDocumentId={selectedDocumentId}
        title="Needs agent review"
        onSelectDocument={onSelectDocument}
        onToggle={onToggle}
      />
      <DocumentCategory
        defaultExpanded={false}
        folders={otherFolders}
        isExpanded={isExpanded}
        selectedDocumentId={selectedDocumentId}
        title="Other Documents"
        onSelectDocument={onSelectDocument}
        onToggle={onToggle}
      />
    </VStack>
  );
};
